import React, { useState, useEffect, useRef } from "react";
// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";

import { API_URI } from "../../helpers";

// Formatea fecha/hora en timezone Argentina (UTC-3), igual que en Ventas.
const formatDateTimeAR = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

// Etiqueta + color del estado del pago.
const STATUS_META = {
  confirmed: { label: "Confirmado", bg: "#DBEAFE", color: "#1E40AF" },
  assigned: { label: "Asignado", bg: "#DCFCE7", color: "#166534" },
};
const statusBadge = (status) => {
  const meta = STATUS_META[status] || { label: status || "—", bg: "#F3F4F6", color: "#374151" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: meta.bg,
        color: meta.color,
      }}
    >
      {meta.label}
    </span>
  );
};

// Etiqueta de tarjeta: "visa •••• 4242".
const cardLabel = (v) => {
  if (!v.cardLast4) return "";
  const brand = v.cardBrand ? v.cardBrand : "";
  return `${brand} •••• ${v.cardLast4}`.trim();
};

// Presets de período. Setean startDate/endDate contra created_at en el backend.
const RANGES = [
  { key: "all", label: "Todo" },
  { key: "7", label: "7 días" },
  { key: "14", label: "14 días" },
  { key: "30", label: "30 días" },
  { key: "year", label: "Último año" },
  { key: "custom", label: "Personalizado" },
];

// Fecha local -> "YYYY-MM-DD" (sin saltos de timezone como toISOString).
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const dateInputStyle = {
  padding: "6px 10px",
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  fontSize: 13,
  color: "#374151",
  outline: "none",
};

const exportBtnStyle = (disabled) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 14px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  border: "1px solid #D1D5DB",
  background: "#fff",
  color: "#374151",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const HEADERS = [
  { name: "Fecha", type: "text", sortable: "created_at" },
  { name: "Estado", type: "text", sortable: "status" },
  { name: "País", type: "text" },
  { name: "Nombre", type: "text" },
  { name: "Apellido", type: "text" },
  { name: "Mail", type: "text", sortable: "mail" },
  { name: "Tarjeta", type: "text" },
  { name: "Método", type: "text" },
  { name: "Local", type: "text", sortable: "price", align: "right" },
  { name: "USD", type: "text", sortable: "price_usd", align: "right" },
];

export default function ListPagos() {
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [downloading, setDownloading] = useState("");
  const [tableData, setTableData] = useState({
    headers: HEADERS,
    data: [],
    sorting: {},
    pagination: null,
  });

  // Evita que el debounce dispare una segunda carga en el montaje inicial.
  const didMount = useRef(false);
  const didMountRange = useRef(false);

  // Devuelve { startDate, endDate } según el período activo.
  function rangeDates() {
    if (range === "all") return { startDate: "", endDate: "" };
    if (range === "custom") return { startDate: customFrom || "", endDate: customTo || "" };
    const today = new Date();
    const start = new Date(today);
    if (range === "year") start.setDate(start.getDate() - 364);
    else start.setDate(start.getDate() - (Number(range) - 1));
    return { startDate: toISO(start), endDate: toISO(today) };
  }

  // Agrega el rango de fechas vigente al body que se manda al backend.
  function appendDateRange(body) {
    const { startDate, endDate } = rangeDates();
    if (startDate) body.append("startDate", startDate);
    if (endDate) body.append("endDate", endDate);
  }

  // Construye la fila visible de la tabla a partir del registro de pago.
  const buildRow = (v) => {
    const studentName = v.student ? v.student.name : "";
    // Solo cuando está asignado hay alumno: el nombre va como link a su ficha.
    const isAssigned = v.status === "assigned" && v.student_id;
    const nameCell = isAssigned
      ? { name: studentName || "(ver alumno)", type: "link", to: "Student-new/" + v.student_id }
      : { name: studentName, type: "text" };

    return [
      { name: formatDateTimeAR(v.created_at), type: "text" },
      { name: statusBadge(v.status), type: "text" },
      { name: v.country ? v.country.name : "", type: "text" },
      nameCell,
      { name: v.student ? v.student.lastname : "", type: "text" },
      { name: v.student && v.student.mail ? v.student.mail : v.mail || "", type: "text" },
      { name: cardLabel(v), type: "text" },
      { name: v.pay_channel ? v.pay_channel.name : "", type: "text" },
      { name: v.price, type: "text", align: "right" },
      { name: v.price_usd, type: "text", align: "right" },
    ];
  };

  function load(params = {}) {
    const body = new FormData();

    const s = params.search !== undefined ? params.search : search;
    if (s && s.trim() !== "") {
      body.append("search", s.trim());
    }

    // Orden: el explícito (al clickear una columna) o el vigente.
    const sort = params.order ? { field: params.order, dir: params.direc } : tableData.sorting;
    if (sort && sort.field) {
      body.append("order", sort.field);
      body.append("direc", sort.dir);
    }

    if (params.page) {
      body.append("page", params.page);
    }

    appendDateRange(body);

    setLoading(true);
    fetch(`${API_URI}/admin/payments`, { method: "POST", body })
      .then((res) => res.json())
      .then((result) => {
        const rows = result.payments.data.map(buildRow);
        setTotal(result.total);
        setTableData((prev) => ({
          ...prev,
          data: rows,
          sorting: params.order ? { field: params.order, dir: params.direc } : prev.sorting,
          pagination: {
            current: result.payments.current_page,
            next: result.payments.next_page_url,
            prev: result.payments.prev_page_url,
            total: result.payments.last_page,
          },
        }));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }

  // Carga inicial: pagos más recientes primero.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Buscador ágil: debounce de 350ms y vuelta a la página 1.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    const t = setTimeout(() => {
      load({ search, page: 1 });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Recarga (a página 1) cuando cambia el período o el rango personalizado.
  useEffect(() => {
    if (!didMountRange.current) {
      didMountRange.current = true;
      return;
    }
    // En "Personalizado" esperamos a que haya al menos una fecha cargada.
    if (range === "custom" && !customFrom && !customTo) return;
    load({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, customFrom, customTo]);

  // Descarga el listado filtrado (mismos filtros que la tabla) en CSV o Excel.
  function download(format) {
    const body = new FormData();
    if (search && search.trim() !== "") body.append("search", search.trim());
    appendDateRange(body);
    body.append("format", format);

    setDownloading(format);
    fetch(`${API_URI}/admin/payments/export`, { method: "POST", body })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const stamp = toISO(new Date());
        a.href = url;
        a.download = `pagos_${stamp}.${format === "xls" ? "xls" : "csv"}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      })
      .catch(() => {})
      .finally(() => setDownloading(""));
  }

  function ordering(field, dir) {
    setTableData((prev) => ({ ...prev, data: [], sorting: { field, dir } }));
    load({ order: field, direc: dir, page: 1 });
  }

  function paginate(params) {
    load({ page: params.page });
  }

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        {/* Buscador inteligente */}
        <Card style={{ marginBottom: 16 }}>
          <CardBody style={{ padding: "20px" }}>
            <div style={{ position: "relative", maxWidth: 720 }}>
              <span
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9CA3AF",
                  fontSize: 20,
                  pointerEvents: "none",
                }}
                className="material-icons"
              >
                search
              </span>
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por mail, nombre, apellido, últimos 4 de la tarjeta, pay_num…"
                style={{
                  width: "100%",
                  padding: "12px 40px 12px 44px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 10,
                  fontSize: 15,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {search ? (
                <span
                  onClick={() => setSearch("")}
                  className="material-icons"
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9CA3AF",
                    fontSize: 20,
                    cursor: "pointer",
                  }}
                >
                  close
                </span>
              ) : null}
            </div>
            <div style={{ marginTop: 10, fontSize: 13, color: "#6B7280" }}>
              {loading
                ? "Buscando…"
                : total !== null
                ? `${total.toLocaleString("es-AR")} ${total === 1 ? "pago" : "pagos"}${
                    search ? ` que coinciden con “${search.trim()}”` : ""
                  }`
                : ""}
            </div>

            {/* Filtro de período + descargas */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 13, color: "#6B7280", marginRight: 2 }}>Período:</span>
              {RANGES.map((r) => {
                const active = range === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setRange(r.key)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: active ? "1px solid #2563EB" : "1px solid #D1D5DB",
                      background: active ? "#2563EB" : "#fff",
                      color: active ? "#fff" : "#374151",
                    }}
                  >
                    {r.label}
                  </button>
                );
              })}

              {range === "custom" ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 4 }}>
                  <input
                    type="date"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    style={dateInputStyle}
                  />
                  <span style={{ color: "#9CA3AF" }}>–</span>
                  <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(e) => setCustomTo(e.target.value)}
                    style={dateInputStyle}
                  />
                </span>
              ) : null}

              <span style={{ flex: 1 }} />

              <button
                type="button"
                onClick={() => download("csv")}
                disabled={!!downloading}
                style={exportBtnStyle(!!downloading)}
              >
                <span className="material-icons" style={{ fontSize: 18 }}>
                  download
                </span>
                {downloading === "csv" ? "Generando…" : "CSV"}
              </button>
              <button
                type="button"
                onClick={() => download("xls")}
                disabled={!!downloading}
                style={exportBtnStyle(!!downloading)}
              >
                <span className="material-icons" style={{ fontSize: 18 }}>
                  download
                </span>
                {downloading === "xls" ? "Generando…" : "Excel"}
              </button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Table
              tableHeaderColor="info"
              tableHead={tableData.headers}
              tableData={tableData.data}
              sortered={tableData.sorting}
              sortCallback={ordering}
              paginate={paginate}
              dtaPage={tableData.pagination}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
